<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up" style="width:640px">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <h2 class="sb-modal-title">{{ metric.Name }}</h2>
            <p class="sb-intro">{{ ownerName }} · goal {{ goalText(metric, weeks[weeks.length - 1]) }} · on goal {{ onGoalCount }} of the last {{ enteredCount }} weeks</p>

            <!-- Last 13 weeks against the goal in effect each week. Keeps its full size on phones and
                 scrolls sideways, so the labels stay readable. -->
            <div class="sb-chart-scroll">
                <svg :viewBox="`0 0 ${W} ${H}`" class="sb-chart" role="img" :aria-label="metric.Name + ', last 13 weeks against goal'">
                    <template v-for="t in axis.ticks" :key="'t' + t">
                        <line :x1="P.l" :x2="W - P.r" :y1="y(t)" :y2="y(t)" class="sb-grid" />
                        <text :x="P.l - 6" :y="y(t) + 4" text-anchor="end" class="sb-axis">{{ formatShort(metric, t) }}</text>
                    </template>
                    <template v-for="(bar, i) in bars" :key="'b' + i">
                        <rect v-if="bar.value === null" :x="x(i) - bw * .3" :y="y(axis.min) - 3" :width="bw * .6" height="3" class="sb-bar-none" />
                        <rect v-else :x="x(i) - bw * .3" :y="y(bar.value)" :width="bw * .6" :height="Math.max(2, y(axis.min) - y(bar.value))" rx="3" :class="bar.pass ? 'sb-bar-pass' : 'sb-bar-fail'">
                            <title>{{ weekLabel(bar.week) }}: {{ formatValue(metric, bar.value) }} (goal {{ goalText(metric, bar.week) }})</title>
                        </rect>
                    </template>
                    <path :d="goalPath('Goal')" class="sb-goal-line" />
                    <path v-if="hasRange" :d="goalPath('GoalMax')" class="sb-goal-line" />
                    <template v-for="(week, i) in weeks" :key="'l' + i">
                        <text v-if="i % 2 == 0 || i == weeks.length - 1" :x="P.l + i * bw + bw / 2" :y="H - 8" text-anchor="middle" class="sb-axis">{{ weekLabel(week) }}</text>
                    </template>
                </svg>
            </div>
            <div class="sb-legend">
                <span><i class="sb-lg-pass"></i>On goal</span>
                <span><i class="sb-lg-fail"></i>Off goal</span>
                <span><svg width="22" height="8"><line x1="0" x2="22" y1="4" y2="4" class="sb-goal-line" /></svg> Goal</span>
            </div>
            <p v-if="metric.Definition" class="sb-intro" style="margin-top:12px">{{ metric.Definition }}</p>

            <div class="sb-actions">
                <span style="flex:1"></span>
                <div class="btn-light" @click="$emit('enter')">Enter or Fix Weeks</div>
                <div class="btn" @click="$emit('close')">Done</div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { passes, goalAt, goalText, formatValue, formatShort, weekLabel } from '@/scoreboard';

// A round gridline step near raw: 1, 2, 2.5 or 5 times a power of ten
function niceStep(raw: number): number {
    const power = Math.pow(10, Math.floor(Math.log10(raw)));
    const f = raw / power;
    return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * power;
}

export default defineComponent({
    name: 'ScoreboardTrendModal',
    props: [ 'metric', 'weeks', 'ownerName' ],
    emits: [ 'close', 'enter' ],
    data() {
        return {
            W: 560,
            H: 210,
            P: { l: 64, r: 14, t: 16, b: 28 }
        }
    },
    computed: {
        bars(): any[] {
            return this.weeks.map((week: string) => {
                const v = this.metric.Values[week];
                const value = v === undefined || v === null ? null : Number(v);
                return { week, value, pass: passes(this.metric, value, week) };
            });
        },
        goals(): any[] {
            return this.weeks.map((week: string) => goalAt(this.metric, week));
        },
        hasRange(): boolean {
            return this.goals.some((g: any) => g && g.Cmp == 'range');
        },
        // Zoomed to the numbers and goals with a little room around them, on round steps (3 to 5
        // gridlines). Never below zero unless something is negative.
        axis(): { min: number, max: number, ticks: number[] } {
            const nums = this.bars.filter((b: any) => b.value !== null).map((b: any) => b.value)
                .concat(this.goals.filter((g: any) => g).map((g: any) => Number(g.Goal)))
                .concat(this.goals.filter((g: any) => g && g.Cmp == 'range').map((g: any) => Number(g.GoalMax)));
            let hi = Math.max(...nums), lo = Math.min(...nums);
            if(hi == lo) {
                const spread = Math.abs(hi) * .1 || 1;
                hi += spread;
                lo -= spread;
            }
            const room = (hi - lo) * .1;
            const step = niceStep((hi - lo + room * 2) / 4);
            let min = Math.floor((lo - room) / step) * step;
            const max = Math.ceil((hi + room) / step) * step;
            if(Math.min(...nums) >= 0) {
                min = Math.max(0, min);
            }
            const ticks = [];
            for(let t = min; t <= max + step / 2; t += step) {
                ticks.push(Number(t.toPrecision(12)));
            }
            return { min, max, ticks };
        },
        bw(): number {
            return (this.W - this.P.l - this.P.r) / this.weeks.length;
        },
        enteredCount(): number {
            return this.bars.filter((b: any) => b.value !== null).length;
        },
        onGoalCount(): number {
            return this.bars.filter((b: any) => b.pass).length;
        }
    },
    methods: {
        goalText,
        formatValue,
        formatShort,
        weekLabel,
        x(i: number): number {
            return this.P.l + i * this.bw + this.bw / 2;
        },
        y(v: number): number {
            const { min, max } = this.axis;
            return this.P.t + (this.H - this.P.t - this.P.b) * (1 - (v - min) / (max - min));
        },
        // A stepped line, so a goal change shows in the week it took effect
        goalPath(field: string): string {
            return this.goals.map((g: any, i: number) => {
                const v = g ? Number(g[field] ?? g.Goal) : 0;
                return `${i ? 'L' : 'M'}${this.P.l + i * this.bw} ${this.y(v)} L${this.P.l + (i + 1) * this.bw} ${this.y(v)}`;
            }).join(' ');
        }
    }
});
</script>

<style scoped>
.sb-modal-title { margin: 0 0 6px; font-size: 20px; }
.sb-intro { font-size: 13.5px; color: var(--font-dark-gray); margin: 0 0 10px; }
.sb-chart-scroll { overflow-x: auto; }
.sb-chart { width: 100%; min-width: 520px; height: auto; display: block; }
.sb-grid { stroke: var(--light-gray); }
.sb-axis { font-size: 10px; fill: var(--font-dark-gray); }
.sb-bar-pass { fill: #2E7D46; }
.sb-bar-fail { fill: var(--primary-color); }
.sb-bar-none { fill: var(--mid-gray); }
.sb-goal-line { stroke: var(--dark-gray); stroke-dasharray: 5 4; stroke-width: 1.6; fill: none; }
.sb-legend { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; font-size: 12px; color: var(--font-dark-gray); margin-top: 6px; }
.sb-legend i { display: inline-block; width: 11px; height: 11px; border-radius: 3px; margin-right: 5px; vertical-align: -2px; }
.sb-lg-pass { background: #2E7D46; }
.sb-lg-fail { background: var(--primary-color); }
.sb-actions { display: flex; align-items: center; gap: 10px; margin-top: 20px; }
.sb-actions .btn, .sb-actions .btn-light { margin: 0; padding: 8px 18px; }
</style>
